import { clsx } from "clsx";
import { without } from "ramda";
import { type FC, useState } from "react";
import { useHover, usePress } from "react-aria";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  HStack,
  Icons,
  Item,
  MenuOptionItem,
  MenuOptionItemIntent,
  MenuTriggerButton,
  Section,
  Spacer,
  SymbolIcon,
  TriggerButtonSize,
} from "widgets";

import { BrowserTab } from "@/app-utils/browser-tab.ts";
import { IndexStatus, type InputModifyProjectFileInput, type ProjectFile } from "@/graphql.js";
import type { IdentifiableFileData } from "@/modules/files/components/FileBrowser.js";
import { FilePreviewControlledDialog } from "@/modules/files/components/FilePreviewControlledDialog.js";
import { FilesDeletionControlledAlertDialog } from "@/modules/files/components/FilesDeletionControlledAlertDialog.js";
import type { FileNameCellParams } from "@/modules/files/components/FileSearchResults.js";
import type { BrowsingProjectFileFragment } from "@/modules/files/graphql/files-queries.gql.ts";
import {
  isAlignmentFile,
  isAnalysisSetFile,
  isBamFile,
  isFastaFile,
  isGenomeViewerFile,
} from "@/modules/files/utils/file.js";
import { notify } from "@/modules/notifications/notify.tsx";
import {
  downloadProjectFile,
  getGenomeViewerMenuItemDisabledHint,
  getProjectFilesDataForSendingToGenomeViewer,
  indexBamFile,
  storeFilesToBeCopied,
  storeFilesToBeCut,
  updateProjectFile,
} from "@/redux/modules/files.js";
import { forwardToAnalyzer } from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.js";
import { ANALYZERS, ENTITY_TYPE, FILE_BROWSE_TYPES } from "@/settings/settings.ts";
import { EllipsedContent } from "@/ui-modules/core/grid/cells/core/EllipsedContent.js";
import BamFileValidationDialog from "@/ui-modules/files/BamFileValidationDialog.js";
import { FileDetailsDialog } from "@/ui-modules/files/FileDetailsDialog.tsx";
import { FileEditDialogForm } from "@/ui-modules/files/FileEditDialog.tsx";

import { FileIcon } from "../../FileIcon.js";
import { SmartLink } from "../../SmartLink.js";
import type { IAgGridCellProps } from "../cell-base-interfaces.js";
import style from "../styles/Cells.module.scss";
import { FileIndexStatusIcon } from "./fileIcons/FileIndexStatusIcon.js";
import { ProtectedFileIcon } from "./fileIcons/ProtectedFileIcon.js";

export const FileNameCell: FC<IAgGridCellProps & FileNameCellParams> = (props) => {
  const data: IdentifiableFileData = props.data;
  const fileName = data.file.fileName; //props.value is now "file" instead of "file.fileName"
  const newIndexingStatus = useSelector<IAppState, IndexStatus | null>((state) =>
    data.file.id ? state.files.indexingStatusLookup[data.file.id] : null,
  );

  const dispatch = useDispatch<ReduxDispatch>();
  const navigate = useNavigate();

  const { hoverProps, isHovered } = useHover({});

  const [projectFilesForGenomeViewer, setProjectFilesForGenomeViewer] = useState<ProjectFile[] | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showFileInfoDialog, setShowFileInfoDialog] = useState(false);
  const [showFileEditDialog, setShowFileEditDialog] = useState(false);
  const [showFileFrame, setShowFileFrame] = useState(false);

  if (!fileName) {
    return null;
  }
  const isAboutToBeMoved = !!data.filesToBeMoved?.find((movedFile) => movedFile.fullPath === data.file.fullPath);
  const genomeViewerMenuItemDisabledHint = getGenomeViewerMenuItemDisabledHint(data.file as ProjectFile);

  const disabledKeys = without(
    [""],
    [
      genomeViewerMenuItemDisabledHint ? "file-menu-genomeviewer" : "",
      !data.file.isEditable ? "file-menu-delete" : "",
      data.file.indexingStatus === IndexStatus.Indexing ? "file-menu-create-index" : "",
    ],
  );

  return (
    <>
      <HStack
        className={clsx(style.stack_cell, { [style.cell_file_about_to_be_moved]: isAboutToBeMoved })}
        domProps={hoverProps}
      >
        <CellIcon
          type={data.file.fileBrowseType as FILE_BROWSE_TYPES}
          filename={data.file.fileName}
          onPress={
            data.file.previewable
              ? () => {
                  setShowFileFrame(true);
                }
              : undefined
          }
          isCellHovered={!!data.file.previewable && isHovered}
        />
        {(data.file.fileBrowseType as FILE_BROWSE_TYPES) === FILE_BROWSE_TYPES.HYPERLINK ? (
          <>
            <SmartLink to={data.file.url} tooltip={fileName} forceTooltip={true}>
              {fileName}
            </SmartLink>
            <Spacer />
          </>
        ) : (
          <EllipsedContent tooltip={fileName} isDisabled={props.context?.errorMode ?? false}>
            {fileName}
          </EllipsedContent>
        )}
        <FileIndexStatusIcon file={data.file} newIndexingStatus={newIndexingStatus} />
        <ProtectedFileIcon file={data.file} />
        {showMenu(data.file) && (
          <MenuTriggerButton
            icon="more_vert"
            label=""
            aria-label="File menu"
            size={TriggerButtonSize.Small}
            onAction={(key) => {
              const fileInput: InputModifyProjectFileInput = {
                id: data.file.id,
                name: data.file.fileName,
                url: data.file.url,
                description: data.file.description,
                organismIds: data.file.organisms?.map((o) => o?.id),
                isProtected: !data.file.isProtected,
              };

              switch (key) {
                case "file-menu-protect":
                  void dispatch(updateProjectFile(fileInput)).then(() => data.refresh?.());
                  break;
                case "file-menu-link":
                  if (data.file.url) {
                    // open in new tab
                    window.open(data.file.url);
                  }
                  break;
                case "file-menu-dataviewer":
                  navigate(`/visualizer/data/dataset/${data.file.id}`);
                  break;
                case "file-menu-genomeviewer":
                  setProjectFilesForGenomeViewer(dispatch(getProjectFilesDataForSendingToGenomeViewer([data.file.id])));
                  break;
                case "file-menu-create-index":
                  dispatch(indexBamFile(data.file.id));
                  break;
                case "file-menu-info":
                  setShowFileInfoDialog(true);
                  break;
                case "file-menu-edit":
                  setShowFileEditDialog(true);
                  break;
                case "file-menu-show":
                  setShowFileFrame(true);
                  break;
                case "file-menu-create-share-link":
                  navigator.clipboard
                    .writeText(BrowserTab.createUrl(`/content/files?file=${data?.file?.id}`))
                    .then((_r) => notify.info({ title: "Link Copied!" }, { autoClose: 500 }));
                  break;
                case "file-menu-download":
                  dispatch(downloadProjectFile(data.file));
                  break;
                case "file-menu-copy":
                  dispatch(storeFilesToBeCopied([data.file]));
                  break;
                case "file-menu-cut":
                  dispatch(storeFilesToBeCut([data.file]));
                  break;
                case "file-menu-delete":
                  setShowDeleteDialog(true);
                  break;
                case "file-menu-sequence-analyzer":
                  void dispatch(
                    forwardToAnalyzer(null, data.file.id, ENTITY_TYPE.PROJECT_FILE, ANALYZERS.SEQUENCE_ANALYZER),
                  );
                  break;
              }
            }}
            disabledKeys={disabledKeys}
          >
            {(data.file.fileBrowseType as FILE_BROWSE_TYPES) === FILE_BROWSE_TYPES.DIRECTORY &&
              data.file.canChangeProtectedStatus && (
                <Section>
                  <Item key={"file-menu-protect"}>
                    <MenuOptionItem
                      id="file-menu-protect"
                      title={data.file.isProtected ? "Allow All Users to Edit" : "Restrict Editing"}
                      icon={data.file.isProtected ? "how_to_reg" : "security"}
                      description={data.file.isProtected ? undefined : "to project managers and admins"}
                    />
                  </Item>
                </Section>
              )}
            {(data.file.fileBrowseType as FILE_BROWSE_TYPES) === FILE_BROWSE_TYPES.HYPERLINK && (
              <Section>
                <Item key="file-menu-link">
                  <MenuOptionItem
                    id="file-menu-link"
                    title="Open in a New Tab"
                    icon={"open_in_new"}
                    intent={MenuOptionItemIntent.Primary}
                  />
                </Item>
              </Section>
            )}
            {(data.file.fileBrowseType as FILE_BROWSE_TYPES) === FILE_BROWSE_TYPES.REGULAR_FILE &&
              isAnalysisSetFile(data.file) && (
                <Section>
                  <Item key="file-menu-dataviewer">
                    <MenuOptionItem
                      id="file-menu-dataviewer"
                      icon={"bar_chart"}
                      intent={MenuOptionItemIntent.Primary}
                      title="Send to Analytical Data Viewer"
                    />
                  </Item>
                </Section>
              )}
            {(data.file.fileBrowseType as FILE_BROWSE_TYPES) === FILE_BROWSE_TYPES.REGULAR_FILE &&
              isGenomeViewerFile(data.file) && (
                <Section>
                  {isGenomeViewerFile(data.file) && (
                    <Item key="file-menu-genomeviewer">
                      <MenuOptionItem
                        id="file-menu-genomeviewer"
                        icon={<img src={Icons.Action.GenomeViewer} width={20} height={20} alt="Genome Viewer" />}
                        intent={MenuOptionItemIntent.Primary}
                        title="Send to Genome Viewer"
                        description={genomeViewerMenuItemDisabledHint}
                      />
                    </Item>
                  )}
                  {isBamFile(data.file) && data.file.indexingStatus !== IndexStatus.Exists && (
                    <Item key="file-menu-create-index">
                      <MenuOptionItem id="file-menu-create-index" icon={"list_alt_add"} title="Create Index" />
                    </Item>
                  )}
                </Section>
              )}
            {(data.file.fileBrowseType as FILE_BROWSE_TYPES) === FILE_BROWSE_TYPES.REGULAR_FILE &&
              (isAlignmentFile(data.file) || isFastaFile(data.file)) && (
                <Section>
                  <Item key="file-menu-sequence-analyzer">
                    <MenuOptionItem
                      id="file-menu-sequence-analyzer"
                      icon={
                        <img src={Icons.Action.GenomeViewer} width={20} height={20} alt={ANALYZERS.SEQUENCE_ANALYZER} />
                      }
                      intent={MenuOptionItemIntent.Primary}
                      title={`Send to ${ANALYZERS.SEQUENCE_ANALYZER}`}
                    />
                  </Item>
                </Section>
              )}
            <Section>
              <Item key="file-menu-info">
                <MenuOptionItem id="file-menu-info" title="Show Details" icon={"info"} />
              </Item>
              {data.file.isEditable && !props.fileSearchContext && (
                <Item key="file-menu-edit">
                  <MenuOptionItem id="file-menu-edit" title="Edit Details" icon={"edit"} />
                </Item>
              )}
              {data.file.previewable && (
                <Item key="file-menu-show">
                  <MenuOptionItem id="file-menu-show" title="Preview" icon={"zoom_in"} />
                </Item>
              )}
            </Section>
            <Section>
              {window.isSecureContext && !!navigator.clipboard && (
                <Item key="file-menu-create-share-link">
                  <MenuOptionItem
                    id="file-menu-create-share-link"
                    title="Copy Link to Clipboard"
                    icon={"content_copy"}
                  />
                </Item>
              )}
              {(data.file.fileBrowseType as FILE_BROWSE_TYPES) === FILE_BROWSE_TYPES.REGULAR_FILE && (
                <Item key="file-menu-download">
                  <MenuOptionItem id="file-menu-download" title="Download" icon={"download"} />
                </Item>
              )}
            </Section>
            {!props.fileSearchContext && (
              <Section>
                <Item key="file-menu-copy">
                  <MenuOptionItem id="file-menu-copy" title="Copy" icon={"file_copy"} />
                </Item>
                {data.file.isEditable && (
                  <Item key="file-menu-cut">
                    <MenuOptionItem id="file-menu-cut" title="Cut" icon={"content_cut"} />
                  </Item>
                )}
              </Section>
            )}
            {data.file.deletable && !props.fileSearchContext && (
              <Section>
                <Item key="file-menu-delete">
                  <MenuOptionItem
                    id="file-menu-delete"
                    title="Delete"
                    intent={MenuOptionItemIntent.Danger}
                    icon={"delete"}
                  />
                </Item>
              </Section>
            )}
          </MenuTriggerButton>
        )}
      </HStack>
      {showFileInfoDialog && (
        <FileDetailsDialog
          onCancel={() => {
            setShowFileInfoDialog(false);
          }}
          fileId={data.file.id}
          onSaved={() => {
            void data.refresh?.();
          }}
        />
      )}
      {showFileEditDialog && (
        <FileEditDialogForm
          onSaved={() => {
            void data.refresh?.();
            setShowFileEditDialog(false);
          }}
          onCancel={() => {
            setShowFileEditDialog(false);
          }}
          fileId={data.file.id}
        />
      )}
      {showFileFrame && (
        <FilePreviewControlledDialog
          onCancel={() => {
            setShowFileFrame(false);
          }}
          fileId={data.file.id}
        />
      )}
      {projectFilesForGenomeViewer && (
        <BamFileValidationDialog
          projectFiles={projectFilesForGenomeViewer}
          onFileAssociationsUpdated={() => {
            void data.refresh?.();
          }}
          onClose={() => {
            setProjectFilesForGenomeViewer(null);
          }}
        />
      )}
      {showDeleteDialog && (
        <FilesDeletionControlledAlertDialog
          selectedFiles={[data.file]}
          onCancel={() => setShowDeleteDialog(false)}
          onDeleted={() => {
            setShowDeleteDialog(false);
            void data.refresh?.();
          }}
        />
      )}
    </>
  );
};

interface CellIconProps {
  type?: FILE_BROWSE_TYPES;
  filename?: string;
  onPress?: () => void;
  isCellHovered?: boolean;
}

const CellIcon: FC<CellIconProps> = (props) => {
  const { pressProps } = usePress({
    onPress: props.onPress,
  });

  const icon = () => {
    switch (props.type) {
      case FILE_BROWSE_TYPES.PROJECT_GROUP:
        return <img src={Icons.Action.AllProjectsBW} width={20} height={20} alt="" />;
      case FILE_BROWSE_TYPES.DIRECTORY:
        return <SymbolIcon icon={"folder"} />;
      case FILE_BROWSE_TYPES.HYPERLINK:
        return <SymbolIcon icon={"link"} />;
      default:
        return <FileIcon filename={props.filename} size={"small"} />;
    }
  };

  return props.onPress ? (
    <div
      role="button"
      {...pressProps}
      className={clsx(style.icon, { [style.icon_cell_hovered]: props.isCellHovered })}
      style={{ cursor: "zoom-in" }}
    >
      {icon()}
    </div>
  ) : (
    <div className={style.icon}>{icon()}</div>
  );
};

function showMenu(file: BrowsingProjectFileFragment): boolean {
  return (
    file.fileBrowseType === FILE_BROWSE_TYPES.DIRECTORY ||
    file.fileBrowseType === FILE_BROWSE_TYPES.REGULAR_FILE ||
    file.fileBrowseType === FILE_BROWSE_TYPES.HYPERLINK
  );
}
